/**
 * CacheStorage abstraction — allows the MarketDataService to run with
 * different backing stores. In the miniapp we only need the browser store,
 * but keeping the abstraction lets us re-use the same ResultCache impl.
 *
 *   InMemoryCacheStorage     → fallback when localStorage is unavailable
 *   LocalStorageCacheStorage → browser (hook usage; persists across reloads)
 */

export interface CacheEntry<T> {
  data: T;
  fetchedAt: number;
}

export interface CacheStorage {
  getEntry<T>(key: string): CacheEntry<T> | null;
  setEntry<T>(key: string, entry: CacheEntry<T>): void;
  removeEntry(key: string): void;
}

export class InMemoryCacheStorage implements CacheStorage {
  private readonly store = new Map<string, CacheEntry<unknown>>();

  getEntry<T>(key: string): CacheEntry<T> | null {
    return (this.store.get(key) as CacheEntry<T> | undefined) ?? null;
  }

  setEntry<T>(key: string, entry: CacheEntry<T>): void {
    this.store.set(key, entry as CacheEntry<unknown>);
  }

  removeEntry(key: string): void {
    this.store.delete(key);
  }
}

function isLocalStorageAvailable(): boolean {
  try {
    return typeof localStorage !== "undefined";
  } catch {
    return false;
  }
}

export class LocalStorageCacheStorage implements CacheStorage {
  constructor(private readonly namespace: string = "mds") {}

  private prefixed(key: string): string {
    return `${this.namespace}:${key}`;
  }

  getEntry<T>(key: string): CacheEntry<T> | null {
    if (!isLocalStorageAvailable()) return null;
    try {
      const raw = localStorage.getItem(this.prefixed(key));
      if (!raw) return null;
      return JSON.parse(raw) as CacheEntry<T>;
    } catch {
      return null;
    }
  }

  setEntry<T>(key: string, entry: CacheEntry<T>): void {
    if (!isLocalStorageAvailable()) return;
    try {
      localStorage.setItem(this.prefixed(key), JSON.stringify(entry));
    } catch {
      // Quota exceeded — silently skip persistence
    }
  }

  removeEntry(key: string): void {
    if (!isLocalStorageAvailable()) return;
    try {
      localStorage.removeItem(this.prefixed(key));
    } catch {
      /* ignore */
    }
  }

  /**
   * Drop expired token-availability rows so old symbols do not linger in
   * localStorage forever when never read again.
   */
  sweepExpiredTokenAvailability(ttlMs: number): void {
    if (!isLocalStorageAvailable()) return;
    const nsPrefix = `${this.namespace}:`;
    const subPrefixes = ["avail:", "avail-all:"];
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const fullKey = localStorage.key(i);
      if (!fullKey?.startsWith(nsPrefix)) continue;

      const relative = fullKey.slice(nsPrefix.length);
      if (!subPrefixes.some((p) => relative.startsWith(p))) continue;

      try {
        const raw = localStorage.getItem(fullKey);
        if (!raw) {
          keysToRemove.push(fullKey);
          continue;
        }
        const parsed = JSON.parse(raw) as { fetchedAt?: unknown };
        const fetchedAt = parsed.fetchedAt;
        if (
          typeof fetchedAt !== "number" ||
          Number.isNaN(fetchedAt) ||
          Date.now() - fetchedAt > ttlMs
        ) {
          keysToRemove.push(fullKey);
        }
      } catch {
        keysToRemove.push(fullKey);
      }
    }

    for (const k of keysToRemove) {
      try {
        localStorage.removeItem(k);
      } catch {
        /* ignore */
      }
    }
  }
}
